import React from 'react';
import { Download } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import { ResultMetric } from './atoms';
import { faceSummaryChip, ThicknessDisplayMode } from './helpers';
import { ThicknessReviewSection } from './ThicknessReviewSection';
import { ThicknessPanelController } from './useCADThicknessPanel';

interface ThicknessResultProps {
  ctrl: ThicknessPanelController;
  uid: string;
  canAnalyze: boolean;
  canApprove: boolean;
}

export const ThicknessResult: React.FC<ThicknessResultProps> = ({
  ctrl,
  uid,
  canAnalyze,
  canApprove,
}) => {
  const {
    t,
    i18n,
    displayedAnalysis,
    result,
    displayMode,
    setDisplayMode,
    thinRegions,
    thickRegions,
    limitations,
    selectedRegion,
    selectRegion,
    clearRegionSelection,
    reporting,
    handleReport,
    handlePdf,
  } = ctrl;

  if (!displayedAnalysis || !result) return null;

  return (
    <>
      <Separator className="my-3" />
      <div className="flex flex-row items-center justify-between">
        <p className="text-sm font-bold text-foreground">
          {t('cad.thickness.result')}
        </p>
        <Badge
          variant={
            result.quality === 'reliable'
              ? 'success'
              : result.quality === 'limited'
                ? 'warning'
                : 'info'
          }
        >
          {t(`cad.thickness.quality.${result.quality}`)}
        </Badge>
      </div>
      {/* Names this run as the authoritative number, against the older geometry
          estimate still shown on the metadata tab. */}
      <p className="mt-1 text-xs text-muted-foreground">
        {t('cad.thickness.qualitySource', {
          engine: displayedAnalysis.evidence?.engineVersion || '-',
          completedAt: new Date(
            displayedAnalysis.completedAt || displayedAnalysis.updatedAt,
          ).toLocaleString(i18n.language.startsWith('en') ? 'en-US' : 'ja-JP'),
        })}
      </p>
      <ToggleGroup
        type="single"
        value={displayMode}
        onValueChange={(next: string) => {
          if (next) setDisplayMode(next as ThicknessDisplayMode);
        }}
        className="mt-2 w-full"
      >
        <ToggleGroupItem value="smooth" className="flex-1">
          {t('cad.thickness.smoothView')}
        </ToggleGroupItem>
        <ToggleGroupItem value="flat" className="flex-1">
          {t('cad.thickness.flatView')}
        </ToggleGroupItem>
      </ToggleGroup>
      <Alert variant="info" className="mt-2">
        <AlertDescription className="text-xs">
          {t('cad.thickness.smoothShadingCaveat', { faceView: t('cad.thickness.flatView') })}
        </AlertDescription>
      </Alert>
      {displayMode === 'flat' && (
        <div
          aria-label={t('cad.thickness.legend.title')}
          className="mt-2 flex flex-wrap gap-2"
        >
          {[
            ['#dc2626', 'thin'],
            ['#22c55e', 'within'],
            ['#2563eb', 'thick'],
            ['#f59e0b', 'tolerance'],
            ['#a0a6af', 'unmeasured'],
          ].map(([color, key]) => (
            <div key={key} className="flex flex-row items-center gap-1">
              {/* These hex values mirror the exact colors the 3D viewer paints
                  each judgment band with, so they stay literal rather than tokenized. */}
              <span
                className="size-2.5 rounded-[2px]"
                style={{ backgroundColor: color }}
              />
              <span className="text-xs">
                {t(`cad.thickness.legend.${key}`)}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-2 flex flex-row gap-2">
        <ResultMetric
          label={t('cad.thickness.thinRegions')}
          value={thinRegions.length}
          valueClassName="text-destructive"
        />
        <ResultMetric
          label={t('cad.thickness.thickRegions')}
          value={thickRegions.length}
          valueClassName="text-primary"
        />
        <ResultMetric
          label={t('cad.thickness.measuredPoints')}
          value={result.measuredCount}
        />
      </div>
      <p className="mt-2 block text-xs text-muted-foreground">
        {t('cad.thickness.pointSupplement', {
          thin: result.finalCounts.thin || 0,
          thick: result.finalCounts.thick || 0,
          isolated: result.isolatedViolationCount,
          rescued: result.toleranceAppliedCount,
        })}
      </p>

      {result.reviewRequiredCount > 0 && (
        <Alert variant="warning" className="mt-2">
          <AlertDescription>
            <span className="block text-xs font-bold">
              {t('cad.thickness.directionReviewRequired', {
                count: result.reviewRequiredCount,
              })}
            </span>
            {Object.entries(result.reviewReasonCounts).map(([reason, count]) => (
              <span key={reason} className="block text-xs">
                ・{t(`cad.thickness.directionReviewReason.${reason}`, {
                  count,
                  defaultValue: `${reason}: ${count}`,
                })}
              </span>
            ))}
          </AlertDescription>
        </Alert>
      )}

      {result.faceSummaries.length > 0 && (
        <details className="mt-3 rounded-md border border-border p-2">
          <summary className="cursor-pointer text-sm font-bold text-foreground">
            {t('cad.thickness.faceSummaries', {
              count: result.faceSummaries.length,
            })}
            <span className="ml-1 text-xs font-normal text-muted-foreground">
              {t('cad.thickness.technicalDetail')}
            </span>
          </summary>
          <ul className="mt-1 max-h-[280px] overflow-y-auto rounded-md border border-border">
            {result.faceSummaries.map((face) => (
              <li
                key={`${face.sourceBodyId || ''}:${face.sourceFaceId}`}
                className="list-none border-b border-border px-2 py-1.5 last:border-b-0"
              >
                <div className="flex flex-row items-center gap-1.5">
                  <Badge variant={faceSummaryChip(face).variant}>
                    {t(
                      `cad.thickness.faceJudgment.${faceSummaryChip(face).key}`,
                    )}
                  </Badge>
                  <span className="text-sm font-semibold">
                    {face.sourceFaceId}
                  </span>
                  {face.sourceBodyId && (
                    <span className="text-xs text-muted-foreground">
                      {face.sourceBodyId}
                    </span>
                  )}
                </div>
                <p className="mt-0.5 block whitespace-pre-line text-xs text-muted-foreground">
                  {t('cad.thickness.faceSummary', {
                    min: face.minMm?.toFixed(2) ?? '-',
                    mean: face.meanMm?.toFixed(2) ?? '-',
                    median: face.medianMm?.toFixed(2) ?? '-',
                    p05: face.p05Mm?.toFixed(2) ?? '-',
                    p95: face.p95Mm?.toFixed(2) ?? '-',
                    measured: face.measuredCount,
                    total: face.measurementCount,
                    thin: face.finalCounts.thin || 0,
                    within: face.finalCounts.within_range || 0,
                    thick: face.finalCounts.thick || 0,
                    unmeasured: face.finalCounts.unmeasured || 0,
                  })}
                </p>
              </li>
            ))}
          </ul>
        </details>
      )}

      {limitations.length > 0 && (
        <Alert variant="warning" className="mt-2">
          <AlertDescription>
            <span className="block text-xs font-bold">
              {t('cad.thickness.limitations')}
            </span>
            {limitations.map((limitation) => (
              <span key={limitation} className="block text-xs">
                {'\u2022 '}
                {t(`cad.thickness.reason.${limitation}`, { defaultValue: limitation })}
              </span>
            ))}
          </AlertDescription>
        </Alert>
      )}

      <div className="mt-2 flex flex-row gap-2">
        <Button
          variant="outline"
          onClick={handleReport}
          disabled={
            Boolean(reporting)
            || displayedAnalysis.status !== 'completed'
          }
          className="flex-1"
        >
          {reporting === 'xlsx' ? <Spinner size="sm" label="" /> : <Download />}
          {t('cad.thickness.downloadReport')}
        </Button>
        <Button
          variant="outline"
          onClick={handlePdf}
          disabled={
            Boolean(reporting)
            || displayedAnalysis.status !== 'completed'
          }
          className="flex-1"
        >
          {reporting === 'pdf' ? <Spinner size="sm" label="" /> : <Download />}
          {t('cad.thickness.downloadPdf')}
        </Button>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-bold text-foreground">
          {t('cad.thickness.violationRegions', { count: result.regions.length })}
        </p>
        <Button
          size="sm"
          variant="outline"
          disabled={!selectedRegion}
          onClick={clearRegionSelection}
        >
          {t('cad.thickness.clearSelection')}
        </Button>
      </div>
      {result.regions.length > 0 ? (
        <ul className="mt-1 flex flex-col">
          {result.regions.map((region) => (
            <li key={region.regionId}>
              <button
                type="button"
                onClick={() => selectRegion(region)}
                aria-pressed={region.regionId === selectedRegion?.regionId}
                className={cn(
                  'mb-1 w-full rounded-md px-2 py-1.5 text-left hover:bg-accent',
                  region.regionId === selectedRegion?.regionId && 'bg-accent',
                )}
              >
                <div className="flex flex-row items-center gap-1">
                  <Badge variant={region.judgment === 'thin' ? 'destructive' : 'info'}>
                    {region.judgment === 'thin'
                      ? t('cad.thickness.thin')
                      : t('cad.thickness.thick')}
                  </Badge>
                  <span className="text-sm font-semibold">
                    {region.regionId}
                  </span>
                  <Badge variant="outline">
                    {t(`cad.thickness.regionKind.${region.kind}`)}
                  </Badge>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {t('cad.thickness.regionSummary', {
                    min: region.minMm.toFixed(2),
                    mean: region.meanMm.toFixed(2),
                    max: region.maxMm.toFixed(2),
                    count: region.count,
                    x: region.centroid[0].toFixed(2),
                    y: region.centroid[1].toFixed(2),
                    z: region.centroid[2].toFixed(2),
                    extremeX: region.extremePoint[0].toFixed(2),
                    extremeY: region.extremePoint[1].toFixed(2),
                    extremeZ: region.extremePoint[2].toFixed(2),
                    faceCount: region.faceIndices.length,
                    sourceFaceCount: region.sourceFaceIds.length,
                    sourceBodyCount: region.sourceBodyIds.length,
                  })}
                </p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {t('cad.thickness.regionCentroidPrecise', {
                    x: region.centroid[0].toFixed(3),
                    y: region.centroid[1].toFixed(3),
                    z: region.centroid[2].toFixed(3),
                  })}
                  <br />
                  {t(
                    region.judgment === 'thin'
                      ? 'cad.thickness.regionExtremeThin'
                      : 'cad.thickness.regionExtremeThick',
                    {
                      x: region.extremePoint[0].toFixed(3),
                      y: region.extremePoint[1].toFixed(3),
                      z: region.extremePoint[2].toFixed(3),
                    },
                  )}
                  {region.sourceFaceIds.length > 0 && (
                    <>
                      <br />
                      {t('cad.thickness.regionSourceFaces', {
                        ids: region.sourceFaceIds.join(', '),
                      })}
                    </>
                  )}
                </p>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <Alert variant="success" className="mt-2">
          <AlertDescription>{t('cad.thickness.noViolations')}</AlertDescription>
        </Alert>
      )}

      <ThicknessReviewSection
        ctrl={ctrl}
        uid={uid}
        canAnalyze={canAnalyze}
        canApprove={canApprove}
      />
    </>
  );
};
